import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@kalos/ui';
import { format } from 'date-fns';
import { type FC, useCallback, useState } from 'react';

import { ExportJSON } from '../../ExportJSON';

export type ExportType = 'all' | 'quickbooks';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (type: ExportType) => void;
}

export const TransactionExportDialog: FC<Props> = ({ isOpen, onClose, onConfirm }) => {
  const [exportType, setExportType] = useState<ExportType>('all');

  const handleConfirm = useCallback(() => {
    onConfirm(exportType);
  }, [exportType, onConfirm]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Select Export Type</DialogTitle>
          <DialogDescription>Choose the format for your export</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-4">
          <div className="flex items-center space-x-2">
            <Button
              variant={exportType === 'all' ? 'default' : 'outline'}
              onClick={() => setExportType('all')}
              className="flex-1"
            >
              All visible fields
            </Button>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant={exportType === 'quickbooks' ? 'default' : 'outline'}
              onClick={() => setExportType('quickbooks')}
              className="flex-1"
            >
              QuickBooks Export
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleConfirm}>Export</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface ExportComponentsProps {
  exportPageData: Array<Record<string, unknown>>;
  exportData: Array<Record<string, unknown>>;
  exportType: ExportType;
  exportPageStatus: 'idle' | 'loading' | 'loaded';
  exportStatus: 'idle' | 'loading' | 'loaded';
  onExportPageExported: () => void;
  onExported: () => void;
}

export const TransactionExportComponents: FC<ExportComponentsProps> = ({
  exportPageData,
  exportData,
  exportType,
  exportPageStatus,
  exportStatus,
  onExportPageExported,
  onExported,
}) => {
  return (
    <div style={{ display: 'none' }}>
      <ExportJSON
        json={exportPageData}
        filename={() =>
          `Transactions_Page_${exportType === 'quickbooks' ? 'QuickBooks_' : ''}Export_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}`
        }
        onExported={onExportPageExported}
        status={exportPageStatus}
      />

      <ExportJSON
        json={exportData}
        filename={() =>
          `Transactions_All_${exportType === 'quickbooks' ? 'QuickBooks_' : ''}Export_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}`
        }
        onExported={onExported}
        status={exportStatus}
      />
    </div>
  );
};
